import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable, Draggable, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { Disclosure } from '@headlessui/react';

import { Node } from 'src/common/interfaces/node.interface';
import { Step } from 'src/common/interfaces/step.interface';

import { apiDeleteStep, apiUpdateStep } from 'src/utils/journeyApi';
import { useEditorContext } from 'src/store/editor';
import { EditorSectionOptions } from './options';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { ChevronUpSmallIcon, DotMenuSmallIcon, DragHandleSmallIcon } from 'src/monet/icons';
import { EditorSectionStepCard } from './step-card';
import { Nullable } from 'src/types/nullable.type';
import { useStepEditorContext } from 'src/store/step-editor';
import { LibraryContentCardTypeEnum } from 'src/common/components/library/content-card/type.enum';
import debounce from 'lodash/debounce';
import { EditorSectionName } from './name';
import { getLastNodeInStep } from 'src/utils/journey';
import AddStepToSectionButton from '../components/buttons/add-step-to-section.button';

interface EditorSectionInterfaceProps {
  step: Step;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  isCollapsed: boolean;
  activeNodeId: Nullable<Node['id']>;
}

export const EditorSection = ({ step, activeNodeId, dragHandleProps, isCollapsed }: EditorSectionInterfaceProps) => {
  const disclosureButtonRef = useRef<Nullable<HTMLButtonElement>>(null);
  const { updateSteps, currentActiveNodeId, setCurrentActiveNodeId } = useEditorContext();

  const { setStateFromNode } = useStepEditorContext();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const [keepMenuOpen, setKeepMenuOpen] = useState(false);

  const onSectionNameChange = useCallback(
    async (newName: string) => {
      try {
        const updatedStep = await apiUpdateStep(step.id, { name: newName });
        updateSteps((steps: Step[]) => steps.map((s) => (s.id === step.id ? updatedStep : s)));
      } catch (err: any) {
        setErrorNotification('Section name could not be updated');
      }
    },
    [step.id, updateSteps, setErrorNotification]
  );

  const debouncedSectionNameChangeHandler = useMemo(
    () => debounce((value) => onSectionNameChange(value), 500),
    [onSectionNameChange]
  );

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedSectionNameChangeHandler.cancel();
    };
  }, [debouncedSectionNameChangeHandler]);

  const onSectionDelete = useCallback(() => {
    return apiDeleteStep(step.id).then(() => {
      updateSteps((steps) => steps.filter((s: any) => s.id !== step.id));
      setSuccessNotification('Section succcessfully deleted.');
    });
  }, [step.id, updateSteps, setSuccessNotification]);

  const onMenuOpen = () => {
    setKeepMenuOpen(true);
  };

  const onMenuClose = () => {
    setKeepMenuOpen(false);
  };

  const onStepCardClicked = (node: Node) => () => {
    if (currentActiveNodeId !== node.id) {
      setStateFromNode(node);
      setCurrentActiveNodeId(node.id);
    }
  };

  return (
    <Disclosure as='div' defaultOpen className='w-[336px]'>
      {({ open }) => (
        <div className='flex flex-col relative items-start '>
          <div className='flex grow-0 space-x-xs items-center text-bedrock-black w-full group/section-header'>
            <div className='relative flex items-center space-x-sm w-full'>
              <div {...dragHandleProps} className='flex flex-shrink-0'>
                <DragHandleSmallIcon />
              </div>
              <EditorSectionName
                key={`editable-step-name-${step.id}`}
                className={classNames(
                  'text-bedrock-p-strong w-full rounded-sm bg-bedrock-white',
                  isCollapsed && 'bedrock-dragging-step-ring'
                )}
                onInputChange={debouncedSectionNameChangeHandler}
                value={step.name || 'Name this Section'}
              />
            </div>
            <div className='ml-auto flex space-x-sm'>
              <Disclosure.Button className='has-hover' ref={disclosureButtonRef}>
                <ChevronUpSmallIcon
                  className={classNames(
                    'has-hover transition-all group-hover/section-header:opacity-100',
                    keepMenuOpen ? 'opacity-100' : 'opacity-0',
                    open && '-rotate-180'
                  )}
                />
              </Disclosure.Button>
              <EditorSectionOptions
                renderOrigin={(onClick) => (
                  <div
                    onClick={onClick}
                    className={classNames(
                      'transition-opacity group-hover/section-header:opacity-100',
                      keepMenuOpen ? 'opacity-100' : 'opacity-0'
                    )}
                  >
                    <DotMenuSmallIcon className='has-hover' />
                  </div>
                )}
                onDeleteConfirmClicked={onSectionDelete}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
              />
              <AddStepToSectionButton
                node={getLastNodeInStep(step)}
                step={step}
                className='group-hover/section-header:opacity-100'
              />
            </div>
          </div>

          <Disclosure.Panel
            className={classNames('w-full transition-opacity', isCollapsed ? 'opacity-0' : 'opacity-100')}
          >
            <Droppable key={`droppable-step-${step.id}`} type='step' direction='vertical' droppableId={`${step.id}`}>
              {(provided, snapshot) => (
                <div
                  key={`droppable-${step.id}`}
                  className='flex flex-col scrollbar-hidden mt-md'
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  data-step-id={step.id}
                  data-is-dragging-over={snapshot.isDraggingOver}
                >
                  {step.nodes.map((node: Node, index: number) => {
                    return (
                      <div key={`draggable-node-${node.uuid}`} className='relative' data-node-id={node.id}>
                        <Draggable
                          key={node.uuid}
                          data-node-uuid={node.uuid}
                          draggableId={`${node.uuid}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className='mb-sm'
                              data-is-dragging={snapshot.isDragging}
                              style={{
                                ...provided.draggableProps.style,
                                ...{ cursor: 'default' },
                              }}
                            >
                              <EditorSectionStepCard
                                node={node}
                                step={step}
                                isActive={activeNodeId === node.id}
                                onClick={onStepCardClicked(node)}
                                isDragging={snapshot.isDragging}
                                type={LibraryContentCardTypeEnum.EDITOR}
                              />
                            </div>
                          )}
                        </Draggable>
                      </div>
                    );
                  })}
                  {snapshot.isDraggingOver && provided.placeholder}
                  {snapshot.isDraggingOver && step.nodes.length === 0 && <div className='flex h-12 w-full' />}
                </div>
              )}
            </Droppable>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
};
